import { useState, useEffect } from "react";
import { api, CATEGORIES, PRIORITIES, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { X, Sparkle, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import RecurrenceFields, { defaultRecurrence, buildSeriesPayload } from "./RecurrenceFields";

const MANAGER_ROLES = ["owner", "store_manager", "assistant_manager"];

const empty = { title: "", notes: "", category: "Personal", priority: "Normal", due_date: "", start_time: "", end_time: "", time_frame: "", assigned_to: "", status: "pending", visibility: "private", store_id: "", auto_move_rule: "next_day" };

const VISIBILITY = [
  ["private", "Private — only me (creator)"],
  ["assigned", "Assigned only — assignee & creator"],
  ["store", "Shared — everyone in the store"],
  ["department", "Shared — department"],
  ["company", "Shared — company-wide (all stores)"],
];
const inputCls = "w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const labelCls = "text-xs font-bold uppercase tracking-[0.12em] text-black/60 mb-1 block";

export default function TaskDialog({ open, onClose, onSaved, users, editing }) {
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [stores, setStores] = useState([]);
  const [rec, setRec] = useState(defaultRecurrence());
  const [mode, setMode] = useState("task"); // task | reminder
  const [remDate, setRemDate] = useState("");
  const [remTime, setRemTime] = useState("08:00");

  useEffect(() => {
    if (editing) setForm({ ...empty, ...editing, assigned_to: editing.assigned_to || "" });
    else if (!isManager) setForm({ ...empty, assigned_to: user?.id || "", visibility: "private" });
    else setForm(empty);
    setRec(defaultRecurrence());
    setMode("task");
    setRemDate(new Date().toISOString().slice(0, 10));
    setRemTime("08:00");
  }, [editing, open, isManager, user]);

  useEffect(() => { if (open) api.get("/stores").then((r) => setStores(r.data)).catch(() => {}); }, [open]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const aiOrganize = async () => {
    if (!form.notes && !form.title) { toast.error("Add some notes first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/organize", { notes: `${form.title}\n${form.notes}` });
      setForm((f) => ({ ...f, title: data.title || f.title, notes: data.organized_notes || f.notes, category: data.category || f.category, priority: data.priority || f.priority }));
      toast.success("AI organized your notes");
    } catch (e) { toast.error("AI failed"); } finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (mode === "reminder" && !editing) {
      if (!remDate) { toast.error("Pick a date for the reminder"); return; }
      setBusy(true);
      try {
        await api.post("/reminders", { title: form.title, reminder_date: remDate, reminder_time: remTime || "08:00", reason: form.notes || "" });
        toast.success("Reminder created");
        onSaved();
        onClose();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
      return;
    }
    if (rec.enabled && !editing) {
      if ((rec.type === "weekly" || rec.type === "biweekly") && rec.days.length === 0) {
        toast.error("Pick at least one day to repeat on"); return;
      }
      setBusy(true);
      try {
        await api.post("/recurring/series", buildSeriesPayload(form, rec));
        toast.success("Recurring series created");
        onSaved();
        onClose();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
      return;
    }
    setBusy(true);
    const payload = { ...form, assigned_to: form.assigned_to || null };
    try {
      if (editing) await api.put(`/tasks/${editing.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success(editing ? "Task updated" : "Task created");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const employees = users;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose}>
      <div data-testid="task-dialog" onClick={(e) => e.stopPropagation()} className="bg-white border-2 border-black md:rounded-xl rounded-t-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b-2 border-black px-5 py-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">{editing ? "Edit Task" : (mode === "reminder" ? "New Reminder" : "New Task")}</h2>
          <button data-testid="close-dialog-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center"><X size={18} weight="bold" /></button>
        </div>

        <div className="p-5 space-y-4">
          {!editing && (
            <div data-testid="dialog-mode-toggle" className="grid grid-cols-2 gap-2 p-1 rounded-full border-2 border-black bg-[#F3F4F6]">
              <button data-testid="mode-task-btn" onClick={() => setMode("task")} className={`press h-9 rounded-full text-sm font-bold ${mode === "task" ? "bg-primary text-white" : "bg-transparent"}`}>Task</button>
              <button data-testid="mode-reminder-btn" onClick={() => setMode("reminder")} className={`press h-9 rounded-full text-sm font-bold ${mode === "reminder" ? "bg-primary text-white" : "bg-transparent"}`}>Reminder</button>
            </div>
          )}
          <div>
            <label className={labelCls}>Title</label>
            <input data-testid="task-title-input" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Restock front display" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelCls + " mb-0"}>Notes</label>
              <button data-testid="ai-organize-btn" onClick={aiOrganize} disabled={aiBusy} className="press inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-[#F3E8FF] border-2 border-black">
                <Sparkle size={14} weight="fill" /> {aiBusy ? "Organizing…" : "AI Organize"}
              </button>
            </div>
            <textarea data-testid="task-notes-input" className="w-full min-h-[90px] p-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={mode === "reminder" ? "Optional details…" : "Detailed notes…"} />
          </div>

          {mode === "reminder" && !editing && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Remind me on</label>
                <input data-testid="reminder-date-input" type="date" className={inputCls} value={remDate} onChange={(e) => setRemDate(e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Time</label>
                <input data-testid="reminder-time-input" type="time" className={inputCls} value={remTime} onChange={(e) => setRemTime(e.target.value)} />
              </div>
            </div>
          )}

          {mode === "task" && (<>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Category</label>
              <select data-testid="task-category-select" className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Priority</label>
              <select data-testid="task-priority-select" className={inputCls} value={form.priority} onChange={(e) => set("priority", e.target.value)}>
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Due Date</label>
              <input data-testid="task-due-input" type="date" className={inputCls} value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Assign To</label>
              {isManager ? (
                <select data-testid="task-assign-select" className={inputCls} value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)}>
                  <option value="">Unassigned</option>
                  {employees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              ) : (
                <div data-testid="task-assign-locked" className={inputCls + " flex items-center justify-between text-black/60 bg-[#F3F4F6]"}>
                  <span>{user?.name} (you)</span>
                  <Lock size={16} weight="bold" />
                </div>
              )}
            </div>
          </div>

          {isManager && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Visibility</label>
              <select data-testid="task-visibility-select" className={inputCls} value={form.visibility} onChange={(e) => set("visibility", e.target.value)}>
                {VISIBILITY.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Store</label>
              <select data-testid="task-store-select" className={inputCls} value={form.store_id || ""} onChange={(e) => set("store_id", e.target.value)}>
                <option value="">Default store</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          )}

          <label data-testid="task-autorollover-toggle" className="flex items-start gap-3 p-3 rounded-lg border-2 border-black bg-[#F3F4F6] cursor-pointer">
            <input type="checkbox" className="w-5 h-5 mt-0.5 shrink-0" checked={form.auto_move_rule && form.auto_move_rule !== "none"} onChange={(e) => set("auto_move_rule", e.target.checked ? "next_day" : "none")} />
            <span>
              <span className="font-bold text-sm block">Auto roll over to the next day</span>
              <span className="text-xs text-black/50">If this task isn't completed by its due date, it automatically moves to the next day.</span>
              {form.auto_move_rule && form.auto_move_rule !== "none" && (
                <select data-testid="task-autorollover-mode" className="mt-2 h-9 px-2 rounded-lg border-2 border-black bg-white text-xs font-bold" value={form.auto_move_rule} onChange={(e) => set("auto_move_rule", e.target.value)} onClick={(ev) => ev.stopPropagation()}>
                  <option value="next_day">Every next day</option>
                  <option value="next_weekday">Next working day (skip weekends)</option>
                </select>
              )}
            </span>
          </label>

          {isManager && !editing && (
            <RecurrenceFields value={rec} onChange={setRec} testidPrefix="task-rec" />
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>Start</label>
              <input data-testid="task-start-input" type="time" className={inputCls} value={form.start_time || ""} onChange={(e) => set("start_time", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>End</label>
              <input data-testid="task-end-input" type="time" className={inputCls} value={form.end_time || ""} onChange={(e) => set("end_time", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Timeframe</label>
              <input data-testid="task-timeframe-input" className={inputCls} value={form.time_frame || ""} onChange={(e) => set("time_frame", e.target.value)} placeholder="Morning" />
            </div>
          </div>
          </>)}
        </div>

        <div className="sticky bottom-0 bg-white border-t-2 border-black p-4 flex gap-3">
          <button data-testid="cancel-task-btn" onClick={onClose} className="press flex-1 h-12 rounded-full bg-white font-bold border-2 border-black">Cancel</button>
          <button data-testid="save-task-btn" onClick={save} disabled={busy} className="press flex-1 h-12 rounded-full bg-primary text-white font-bold border-2 border-black disabled:opacity-60">{busy ? "Saving…" : (editing ? "Save Task" : (mode === "reminder" ? "Save Reminder" : (rec.enabled ? "Save Recurring Series" : "Save Task")))}</button>
        </div>
      </div>
    </div>
  );
}
